import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box,
  Button,
  Inscription,
  Preloader,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { ALL_OPTION } from '@/common/constants';
import { OptionKey } from '@/common/types';
import { useFilters } from '@/utils';

import { setExtraKey } from './queries/useClaimsList.query';
import { PageMode } from './ClaimsFilterSection';
import ClaimsList, {
  ACTIVE_PAGE_MODE_SEARCH_PARAM,
  APPEALS_DEFAULT_FILTERS,
  EscalatedSharedFilters,
  SearchKeysForPageMode,
} from './ClaimsList';

enum AppealSubMode {
  ALL = 'ALL',
  OPEN = 'OPEN',
  RESOLVED = 'RESOLVED',
}

interface ButtonProps {
  $isActive?: boolean;
}

const StyledButton = styled(Button)<ButtonProps>`
  padding: ${({ theme }) => `${theme.spacings.spacing10} ${theme.spacings.spacing16}`};
  border-radius: ${({ theme }) => theme.rounds.button1Round};
  background: ${({ $isActive, theme }) => $isActive ? theme.colors.accentContainer : theme.colors.canvas};
  border: ${({ $isActive, theme }) => $isActive ? 'none' : `1px solid ${theme.colors.border1}`};
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: normal;
    color: ${({ $isActive, theme }) => $isActive ? theme.colors.textAccent : theme.colors.textBody};
  }
`;

const TabPanel = styled(Box)<{ $visible: boolean }>`
  ${({ $visible }) => !$visible && 'display: none;'}
`;

interface Props {
  hasAccessToAssignClaim?: boolean;
  setTotal: (val: number | undefined) => void;
  pageMode: PageMode;
  disabled?: boolean;
}

const AppealClaimsList: React.FC<Props> = (props) => {
  const { setTotal, disabled } = props;

  const [totalAll, setTotalAll] = useState<number | undefined>();
  const [totalOpen, setTotalOpen] = useState<number | undefined>();
  const [totalResolved, setTotalResolved] = useState<number | undefined>();
  const [subMode, setSubMode] = useState<AppealSubMode>(AppealSubMode.OPEN);

  const searchKey = `${SearchKeysForPageMode.search}${PageMode.APPEALS}` as const;
  const paymentTypesKey = `${SearchKeysForPageMode.paymentTypes}${PageMode.APPEALS}` as const;
  const statusesKey = `${SearchKeysForPageMode.statuses}${PageMode.APPEALS}` as const;
  const dateRangeKey = `${SearchKeysForPageMode.dateRange}${PageMode.APPEALS}` as const;

  const initialValues = useMemo(() => ({
    [searchKey]: '' as string,
    [paymentTypesKey]: ALL_OPTION.value as OptionKey,
    [statusesKey]: APPEALS_DEFAULT_FILTERS.statuses as OptionKey[],
    [dateRangeKey]: [] as Array<Date | null>,
    [ACTIVE_PAGE_MODE_SEARCH_PARAM]: '' as string,
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtersOptions = useMemo(
    () => ({ autoClear: false as const, disabled }),
    [disabled]
  );

  const { filtersValues, setFilterValue } = useFilters<{
    searchAPPEALS: string;
    paymentTypesAPPEALS: OptionKey;
    statusesAPPEALS: OptionKey[];
    dateRangeAPPEALS: Array<Date | null>;
    [ACTIVE_PAGE_MODE_SEARCH_PARAM]: string;
  }>(initialValues as {
    searchAPPEALS: string;
    paymentTypesAPPEALS: OptionKey;
    statusesAPPEALS: OptionKey[];
    dateRangeAPPEALS: Array<Date | null>;
    [ACTIVE_PAGE_MODE_SEARCH_PARAM]: string;
  }, filtersOptions);

  const activeModeWritten = useRef(false);
  useEffect(() => {
    if (disabled) {
      activeModeWritten.current = false;
    } else if (!activeModeWritten.current) {
      activeModeWritten.current = true;
      setFilterValue[ACTIVE_PAGE_MODE_SEARCH_PARAM](PageMode.APPEALS);
    }
  }, [disabled, setFilterValue]);

  const appealProcessedBySubMode = useMemo(() => {
    if (subMode === AppealSubMode.OPEN) return false;
    if (subMode === AppealSubMode.RESOLVED) return true;
    return undefined;
  }, [subMode]);

  useEffect(() => {
    if (disabled) return;
    setExtraKey({ isAppealed: true, isAppealProcessed: appealProcessedBySubMode });
  }, [disabled, appealProcessedBySubMode]);

  const sharedFilters: EscalatedSharedFilters = useMemo(() => ({
    searchString: filtersValues[searchKey] as string,
    paymentType: filtersValues[paymentTypesKey] as OptionKey,
    statuses: filtersValues[statusesKey] as OptionKey[],
    dateRange: filtersValues[dateRangeKey] as Array<Date | null>,
  }), [filtersValues, searchKey, paymentTypesKey, statusesKey, dateRangeKey]);

  const handleSharedFiltersChange = useCallback((newFilters: EscalatedSharedFilters) => {
    setFilterValue[searchKey](newFilters.searchString);
    setFilterValue[paymentTypesKey](newFilters.paymentType);
    setFilterValue[statusesKey](newFilters.statuses);
    setFilterValue[dateRangeKey](newFilters.dateRange);
  }, [setFilterValue, searchKey, paymentTypesKey, statusesKey, dateRangeKey]);

  const queryEnabled = !disabled;

  const subTabsNode = (
    <Box direction="row" align="center" gap="spacing8">
      <Box>
        <StyledButton
          data-testId="appeals_sub-tab_all"
          label={typeof totalAll === 'undefined' ? (
            <Box direction="row" align="center">
              <Inscription weight={subMode === AppealSubMode.ALL ? 'bold' : 'normal'}>All</Inscription>
              <Box margin={{ left: 'spacing8' }}><Preloader color="iconOnColor" size={12} thinSize={2} /></Box>
            </Box>
          ) : (
            <Inscription weight={subMode === AppealSubMode.ALL ? 'bold' : 'normal'}>
              {`All (${totalAll})`}
            </Inscription>
          )}
          type="button"
          borderless
          $isActive={subMode === AppealSubMode.ALL}
          onClick={() => setSubMode(AppealSubMode.ALL)}
        />
      </Box>

      <Box>
        <StyledButton
          data-testId="appeals_sub-tab_open"
          label={typeof totalOpen === 'undefined' ? (
            <Box direction="row" align="center">
              <Inscription weight={subMode === AppealSubMode.OPEN ? 'bold' : 'normal'}>Open</Inscription>
              <Box margin={{ left: 'spacing8' }}><Preloader color="iconOnColor" size={12} thinSize={2} /></Box>
            </Box>
          ) : (
            <Inscription weight={subMode === AppealSubMode.OPEN ? 'bold' : 'normal'}>
              {`Open (${totalOpen})`}
            </Inscription>
          )}
          type="button"
          borderless
          $isActive={subMode === AppealSubMode.OPEN}
          onClick={() => setSubMode(AppealSubMode.OPEN)}
        />
      </Box>

      <Box>
        <StyledButton
          data-testId="appeals_sub-tab_resolved"
          label={typeof totalResolved === 'undefined' ? (
            <Box direction="row" align="center">
              <Inscription weight={subMode === AppealSubMode.RESOLVED ? 'bold' : 'normal'}>Resolved</Inscription>
              <Box margin={{ left: 'spacing8' }}><Preloader color="iconOnColor" size={12} thinSize={2} /></Box>
            </Box>
          ) : (
            <Inscription weight={subMode === AppealSubMode.RESOLVED ? 'bold' : 'normal'}>
              {`Resolved (${totalResolved})`}
            </Inscription>
          )}
          type="button"
          borderless
          $isActive={subMode === AppealSubMode.RESOLVED}
          onClick={() => setSubMode(AppealSubMode.RESOLVED)}
        />
      </Box>
    </Box>
  );

  return (
    <Box>
      <TabPanel $visible={subMode === AppealSubMode.ALL}>
        <ClaimsList
          {...props}
          isAppealed
          subTabsNode={subMode === AppealSubMode.ALL ? subTabsNode : undefined}
          sharedFilters={sharedFilters}
          onSharedFiltersChange={handleSharedFiltersChange}
          queryEnabled={queryEnabled}
          setTotal={(val) => {
            setTotalAll(val);
          }}
          disabled={disabled || subMode !== AppealSubMode.ALL}
        />
      </TabPanel>

      <TabPanel $visible={subMode === AppealSubMode.OPEN}>
        <ClaimsList
          {...props}
          isAppealed
          isAppealProcessed={false}
          subTabsNode={subMode === AppealSubMode.OPEN ? subTabsNode : undefined}
          sharedFilters={sharedFilters}
          onSharedFiltersChange={handleSharedFiltersChange}
          queryEnabled
          setTotal={(val) => {
            setTotalOpen(val);
            if (val !== undefined) setTotal(val);
          }}
          disabled={disabled || subMode !== AppealSubMode.OPEN}
        />
      </TabPanel>

      <TabPanel $visible={subMode === AppealSubMode.RESOLVED}>
        <ClaimsList
          {...props}
          isAppealed
          isAppealProcessed
          subTabsNode={subMode === AppealSubMode.RESOLVED ? subTabsNode : undefined}
          sharedFilters={sharedFilters}
          onSharedFiltersChange={handleSharedFiltersChange}
          queryEnabled={queryEnabled}
          setTotal={setTotalResolved}
          disabled={disabled || subMode !== AppealSubMode.RESOLVED}
        />
      </TabPanel>
    </Box>
  );
};

export default AppealClaimsList;
